import { EnhancedFormikForm } from '@/components/EnhancedFormikForm';
import { Box, Button, FormikTextInput, Toggle } from '@palmetto/palmetto-components';
import { Field, Formik, FormikHelpers, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { USER_PHONE_NUMBER_PATTERN } from 'types';

import FormSection from '../Forms/FormSection';

export type YourProfileFormSchema = yup.InferType<typeof profileSchema>;
type SubmitHandler = (values: any, formikHelpers: FormikHelpers<any>) => void | Promise<any>;

interface ProfileFormProps {
  initialValues: any;
  handleSubmit: SubmitHandler;
  isLoading: boolean;
  cancelButtonUrl?: string;
}

const profileSchema = yup.object().shape({
  phoneNumber: yup.string().trim().matches(new RegExp(USER_PHONE_NUMBER_PATTERN), {
    message: 'Enter a full phone number, including area code',
    excludeEmptyString: true,
  }),
  familyName: yup.string().trim().required('Last name is required'),
  givenName: yup.string().trim().required('First name is required'),
});

export const YourProfileForm = ({
  initialValues,
  handleSubmit,
  isLoading,
  cancelButtonUrl = '/profile',
}: ProfileFormProps) => {
  return (
    <>
      <Formik
        validationSchema={profileSchema}
        initialValues={initialValues}
        validateOnChange={false}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        <EnhancedFormikForm noValidate>
          <YourProfileFormDetails isLoading={isLoading} cancelButtonUrl={cancelButtonUrl} />
        </EnhancedFormikForm>
      </Formik>
    </>
  );
};

const YourProfileFormDetails = ({
  isLoading,
  cancelButtonUrl = '/profile',
}: Omit<ProfileFormProps, 'initialValues' | 'handleSubmit'>) => {
  const { profileAsSalesRep: profileAsSalesRepFeatureEnabled = false } = useFlags();
  const navigate = useNavigate();
  const { isSubmitting, setFieldValue, values } = useFormikContext<any>();

  return (
    <>
      <FormSection title="Details" description="Your information as saved in LightReach.">
        <Box
          direction={{
            base: 'column',
            tablet: 'row',
          }}
          gap={{ base: 'lg', desktop: 'xl' }}
        >
          <Field
            type="text"
            label="First Name"
            name="givenName"
            id="givenName"
            isRequired
            component={FormikTextInput}
            autoComplete="off"
            isDisabled={isSubmitting || isLoading}
          />
          <Field
            type="text"
            label="Last Name"
            name="familyName"
            id="familyName"
            isRequired
            component={FormikTextInput}
            autoComplete="off"
            isDisabled={isSubmitting || isLoading}
          />
        </Box>
        <Box
          width={{
            base: '100',
            tablet: '50',
          }}
          padding={{
            base: '0 0 0 0',
            tablet: '0 md 0 0',
          }}
        >
          <Field
            type="text"
            label="Phone Number"
            name="phoneNumber"
            id="phoneNumber"
            component={FormikTextInput}
            inputMask="phone"
            autoComplete="off"
            isDisabled={isSubmitting || isLoading}
          />
        </Box>
        <Box>
          <Toggle
            id="userIsSalesRep"
            label="Use as the sales rep when creating new Accounts in LightReach Portal"
            isChecked={values.userIsSalesRep}
            isDisabled={!profileAsSalesRepFeatureEnabled || isSubmitting || isLoading}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setFieldValue('userIsSalesRep', event.target.checked);
            }}
          />
        </Box>
      </FormSection>
      <Box
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        alignItems={{
          base: 'stretch',
          tablet: 'flex-end',
        }}
        justifyContent={{
          tablet: 'flex-end',
        }}
        childGap="sm"
        style={{ flexShrink: 0 }}
        padding="lg 0 0 0"
      >
        <Button
          as="a"
          navigate={() => navigate(cancelButtonUrl)}
          variant="secondary"
          tone="neutral"
          size="md"
          isDisabled={isLoading}
          isLoading={isSubmitting}
        >
          Cancel
        </Button>
        <Button variant="primary" size="md" type="submit" isDisabled={isLoading} isLoading={isSubmitting}>
          Submit
        </Button>
      </Box>
    </>
  );
};
